import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { C } from "../reporting/reportingStyles";
import { successToast, errorToast } from "../../shared/Toast";
import { createBanner, updateBanner } from "../../utils/appBannerApi";
import {
    BANNER_TYPES,
    FREQUENCIES,
    CTA_TYPES,
    CTA_SCREENS,
    QUILL_MODULES,
    QUILL_FORMATS,
    LIMITS,
} from "../../constants/appBanners";

/**
 * Compose / edit an in-app startup banner.
 *
 * One modal for new and existing banners, plain state (no formik) like every other form in
 * this panel. It enforces the same rules the API does — the Arabic both-or-neither rule, the
 * image size and type, the window order — so the admin hears about them while typing rather
 * than on save.
 *
 * The one thing only this screen can explain is the revision rule: changing what a customer
 * SEES shows the banner again to people who already dismissed it, while changing only its
 * schedule does not. That is surfaced as a checkbox whose default follows the same content
 * comparison the server makes, so the two never disagree silently.
 */

const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box",
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6, display: "flex", justifyContent: "space-between" };
const hintStyle = { fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 };
const errStyle = { fontSize: 11, color: C.coral, marginTop: 5, lineHeight: 1.4, fontWeight: 600 };
const sectionStyle = { padding: 14, border: `1px solid ${C.borderLight}`, borderRadius: 12 };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 };
const pill = (active, disabled) => ({
    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    border: `1px solid ${active ? C.coral : C.border}`,
    background: active ? C.coralLight : C.white,
    color: active ? C.coralDark : C.textMed,
});
const btnPrimary = {
    padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, boxShadow: "0 4px 12px rgba(0,150,137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
    background: C.white, color: C.textMed, border: `1px solid ${C.border}`,
};

const Counter = ({ value, max }) => {
    const n = (value || "").length;
    return <span style={{ fontSize: 11, color: n > max ? C.coral : C.textLight, fontWeight: n > max ? 700 : 400 }}>{n} / {max}</span>;
};

/** Quill's empty document is "<p><br></p>" — mirrors isEmptyHtml() on the API side. */
const htmlIsEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === "";

/** ISO → value for <input type="datetime-local"> in the browser's zone. */
function toLocalInput(iso) {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * How the banner looks on a phone.
 *
 * The rich preview renders the admin's own unsaved markup, before the server has sanitised
 * it — the same content Quill is already rendering in the editor two panes away, in their
 * own browser. What is STORED is always the sanitised value.
 */
const PhonePreview = ({ type, title, body, html, imageUrl, ctaLabel, dismissible, dir }) => (
    <div style={{ background: "#0f172a", borderRadius: 24, padding: 10, maxWidth: 300 }}>
        <div style={{ background: C.white, borderRadius: 16, overflow: "hidden", minHeight: 260, display: "flex", flexDirection: "column" }} dir={dir}>
            <div style={{ position: "relative" }}>
                {imageUrl ? (
                    <img src={imageUrl} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }} />
                ) : type === "image" ? (
                    <div style={{ height: 160, background: C.borderLight, display: "flex", alignItems: "center", justifyContent: "center", color: C.textLight, fontSize: 12 }}>
                        Your image goes here
                    </div>
                ) : null}
                {dismissible && (
                    <div style={{ position: "absolute", top: 8, right: 8, width: 24, height: 24, borderRadius: 12, background: "rgba(0,0,0,0.45)", color: C.white, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>×</div>
                )}
            </div>
            {(type !== "image" || ctaLabel) && (
                <div style={{ padding: 14, flex: 1 }}>
                    {type === "rich" ? (
                        html && !htmlIsEmpty(html)
                            ? <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: html }} />
                            : <em style={{ fontSize: 12, color: C.textLight }}>Your formatted text goes here</em>
                    ) : type === "text" ? (
                        <>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                                {title || <em style={{ color: C.textLight, fontWeight: 400 }}>Title</em>}
                            </div>
                            <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                                {body || <em style={{ color: C.textLight }}>Body text</em>}
                            </div>
                        </>
                    ) : null}
                    {ctaLabel && (
                        <div style={{ marginTop: 14, padding: "9px 12px", borderRadius: 10, textAlign: "center", background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, fontSize: 12, fontWeight: 700 }}>
                            {ctaLabel}
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
);

const emptyForm = {
    name: "",
    bannerType: "text",
    titleEn: "", bodyEn: "", htmlEn: "",
    withArabic: false,
    titleAr: "", bodyAr: "", htmlAr: "",
    appId: "",
    ctaType: "none", ctaScreen: "home", ctaUrl: "", ctaLabelEn: "", ctaLabelAr: "",
    activeFrom: "", activeTo: "",
    frequency: "once", frequencyDays: 7,
    sortOrder: 0,
    isActive: true,
    dismissible: true,
    removeImage: false,
};

const fromBanner = (b) => ({
    ...emptyForm,
    name: b.name || "",
    bannerType: b.bannerType || "text",
    titleEn: b.titleEn || "", bodyEn: b.bodyEn || "", htmlEn: b.htmlEn || "",
    withArabic: Boolean(b.titleAr || b.bodyAr || b.htmlAr),
    titleAr: b.titleAr || "", bodyAr: b.bodyAr || "", htmlAr: b.htmlAr || "",
    appId: b.appId ? String(b.appId) : "",
    ctaType: b.cta?.type || "none",
    ctaScreen: b.cta?.screen || "home",
    ctaUrl: b.cta?.url || "",
    ctaLabelEn: b.cta?.labelEn || "",
    ctaLabelAr: b.cta?.labelAr || "",
    activeFrom: b.activeFrom ? toLocalInput(b.activeFrom) : "",
    activeTo: b.activeTo ? toLocalInput(b.activeTo) : "",
    frequency: b.frequency || "once",
    frequencyDays: b.frequencyDays || 7,
    sortOrder: b.sortOrder ?? 0,
    isActive: b.isActive !== false,
    dismissible: b.dismissible !== false,
});

/** The fields a customer sees — the same set the API hashes into contentHash. */
const contentSignature = (f) => JSON.stringify([
    f.bannerType, f.titleEn, f.titleAr, f.bodyEn, f.bodyAr, f.htmlEn, f.htmlAr,
    f.ctaType, f.ctaScreen, f.ctaUrl, f.ctaLabelEn, f.ctaLabelAr,
]);

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number|string} props.merchantId
 * @param {Array<{id,reference,name}>} props.apps   linked apps (from getBannerApps)
 * @param {object|null} props.banner                existing banner, or null for new
 * @param {string} props.imageBase                  base URL to resolve a stored imagePath
 * @param {(saved: object) => void} props.onSaved
 */
const BannerComposerModal = ({ isOpen, onClose, merchantId, apps = [], banner, imageBase = "", onSaved }) => {
    const [form, setForm] = useState(emptyForm);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [imageDims, setImageDims] = useState({ width: null, height: null });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    /**
     * null = follow the content comparison; true/false = the admin overrode it.
     *
     * Deliberately NOT a field on `form`: deriving "did the content move" from form state
     * and then writing the answer back into that same state is a feedback loop, and it
     * showed up as a checkbox and its own explanation disagreeing — each rendered from a
     * different pass. A separate override with no effect behind it cannot drift.
     */
    const [bumpOverride, setBumpOverride] = useState(null);
    const objUrl = useRef(null);
    const [originalSignature, setOriginalSignature] = useState("");

    const isEdit = Boolean(banner?.id);
    const set = (key, v) => { setForm((f) => ({ ...f, [key]: v })); setErrors((e) => ({ ...e, [key]: undefined })); };

    useEffect(() => {
        if (!isOpen) return;
        const next = banner ? fromBanner(banner) : emptyForm;
        setForm(next);
        setOriginalSignature(contentSignature(next));
        setBumpOverride(null);
        setImageFile(null);
        setImagePreview(banner?.imagePath ? `${imageBase}${banner.imagePath}` : "");
        setImageDims({ width: banner?.imageWidth ?? null, height: banner?.imageHeight ?? null });
        setErrors({});
    }, [isOpen, banner, imageBase]);

    useEffect(() => () => { if (objUrl.current) URL.revokeObjectURL(objUrl.current); }, []);

    // The content changed → the banner will be shown again. The same comparison the API
    // makes on `contentHash`, so the checkbox's default and the server's answer agree.
    const contentMoved = isEdit && Boolean(originalSignature) && contentSignature(form) !== originalSignature;
    const imageMoved = isEdit && (Boolean(imageFile) || form.removeImage);
    const willReshow = contentMoved || imageMoved;
    const bumpRevision = bumpOverride === null ? willReshow : bumpOverride;

    const handleImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!LIMITS.imageMimeTypes.includes(file.type)) { errorToast("Image must be a JPEG, PNG or WebP"); return; }
        if (file.size > LIMITS.imageBytes) { errorToast("Image must be 1 MB or smaller"); return; }
        if (objUrl.current) URL.revokeObjectURL(objUrl.current);
        objUrl.current = URL.createObjectURL(file);

        // Read the natural size here: the phone cannot know a remote image's dimensions
        // before it loads, so without these the popup resizes under the customer.
        const probe = new Image();
        probe.onload = () => setImageDims({ width: probe.naturalWidth, height: probe.naturalHeight });
        probe.src = objUrl.current;

        setImageFile(file);
        setImagePreview(objUrl.current);
        set("removeImage", false);
        setErrors((e) => ({ ...e, image: undefined }));
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview("");
        setImageDims({ width: null, height: null });
        set("removeImage", true);
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Give the banner a name (only you see it)";

        if (form.bannerType === "image" && !imagePreview) {
            e.image = "An image banner needs an image";
        }
        if (form.bannerType === "text" && !form.titleEn.trim() && !form.bodyEn.trim()) {
            e.titleEn = "A text banner needs a title or a body";
        }
        if (form.bannerType === "rich" && htmlIsEmpty(form.htmlEn)) {
            e.htmlEn = "Write the message";
        }
        if (form.bannerType === "rich" && (form.htmlEn || "").length > LIMITS.html) {
            e.htmlEn = `Formatted text must be ${Math.round(LIMITS.html / 1024)} KB or smaller`;
        }

        // Arabic: complete or absent. Matches arabicRuleError() on the API side.
        if (form.withArabic) {
            if (form.bannerType === "rich") {
                if (htmlIsEmpty(form.htmlAr)) e.htmlAr = "Write the Arabic version, or turn Arabic off";
            } else {
                if (Boolean(form.titleEn.trim()) !== Boolean(form.titleAr.trim())) {
                    e.titleAr = "Provide the Arabic title too, or turn Arabic off";
                } else if (Boolean(form.bodyEn.trim()) !== Boolean(form.bodyAr.trim())) {
                    e.bodyAr = "Provide the Arabic body too, or turn Arabic off";
                }
            }
        }

        if (form.activeFrom && form.activeTo && new Date(form.activeTo) <= new Date(form.activeFrom)) {
            e.activeTo = "Must be after the start";
        }
        if (form.frequency === "every_n_days" && !(Number(form.frequencyDays) >= 1 && Number(form.frequencyDays) <= LIMITS.maxDays)) {
            e.frequencyDays = `Between 1 and ${LIMITS.maxDays} days`;
        }
        if (form.ctaType !== "none" && !form.ctaLabelEn.trim()) {
            e.ctaLabelEn = "The button needs a label";
        }
        if (form.ctaType === "url" && !/^https?:\/\/\S+$/i.test(form.ctaUrl.trim())) {
            e.ctaUrl = "Enter a full link starting with http:// or https://";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const payload = () => {
        const rich = form.bannerType === "rich";
        const ar = form.withArabic;
        return {
            merchantId,
            appId: form.appId || null,
            name: form.name.trim(),
            bannerType: form.bannerType,
            titleEn: rich ? "" : form.titleEn.trim(),
            bodyEn: rich ? "" : form.bodyEn.trim(),
            titleAr: rich || !ar ? "" : form.titleAr.trim(),
            bodyAr: rich || !ar ? "" : form.bodyAr.trim(),
            htmlEn: rich ? form.htmlEn : "",
            htmlAr: rich && ar ? form.htmlAr : "",
            imageWidth: imageDims.width ?? "",
            imageHeight: imageDims.height ?? "",
            cta: form.ctaType === "none" ? null : {
                type: form.ctaType,
                ...(form.ctaType === "screen" ? { screen: form.ctaScreen } : { url: form.ctaUrl.trim() }),
                labelEn: form.ctaLabelEn.trim(),
                labelAr: ar ? form.ctaLabelAr.trim() : "",
            },
            activeFrom: form.activeFrom ? new Date(form.activeFrom).toISOString() : "",
            activeTo: form.activeTo ? new Date(form.activeTo).toISOString() : "",
            frequency: form.frequency,
            frequencyDays: form.frequency === "every_n_days" ? Number(form.frequencyDays) : "",
            sortOrder: Number(form.sortOrder) || 0,
            isActive: form.isActive,
            dismissible: form.dismissible,
            ...(isEdit ? { removeImage: form.removeImage, bumpRevision } : {}),
        };
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body = payload();
            const saved = isEdit ? await updateBanner(banner.id, body, imageFile) : await createBanner(body, imageFile);
            if (!saved) return;
            successToast(isEdit
                ? (bumpRevision ? "Banner saved — everyone will see it again" : "Banner saved")
                : "Banner created");
            onSaved?.(saved);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const isText = form.bannerType === "text";
    const previewCtaLabel = form.ctaType !== "none" ? (form.ctaLabelEn || "Button") : "";

    return (
        <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} size="xl">
            <ModalHeader toggle={saving ? undefined : onClose}>{isEdit ? "Edit banner" : "New banner"}</ModalHeader>
            <ModalBody>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 330px", gap: 24, alignItems: "start" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        {/* Name + type */}
                        <div>
                            <div style={labelStyle}><span>Banner name</span><span style={{ fontSize: 11, color: C.textLight }}>internal — customers never see it</span></div>
                            <input style={inputStyle} value={form.name} maxLength={LIMITS.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Eid opening hours" />
                            {errors.name && <div style={errStyle}>{errors.name}</div>}
                        </div>

                        <div style={sectionStyle}>
                            <div style={sectionTitle}>What is in it</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {BANNER_TYPES.map((t) => (
                                    <button key={t.value} type="button" onClick={() => set("bannerType", t.value)} style={pill(form.bannerType === t.value)} title={t.hint}>{t.label}</button>
                                ))}
                            </div>
                            <div style={hintStyle}>{BANNER_TYPES.find((t) => t.value === form.bannerType)?.hint}</div>
                        </div>

                        {/* Message */}
                        <div style={sectionStyle}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Message</div>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMed, cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={form.withArabic} onChange={(e) => set("withArabic", e.target.checked)} /> Add Arabic version
                                </label>
                            </div>

                            {form.bannerType === "image" ? (
                                <div style={hintStyle}>An image banner carries no text — everything the customer reads is in the artwork below.</div>
                            ) : (
                                <div style={{ display: "grid", gridTemplateColumns: form.withArabic ? "1fr 1fr" : "1fr", gap: 14 }}>
                                    <div>
                                        {isText ? (
                                            <>
                                                <div style={labelStyle}><span>Title (English)</span><Counter value={form.titleEn} max={LIMITS.title} /></div>
                                                <input style={inputStyle} value={form.titleEn} maxLength={LIMITS.title} onChange={(e) => set("titleEn", e.target.value)} placeholder="New Eid timings" />
                                                {errors.titleEn && <div style={errStyle}>{errors.titleEn}</div>}
                                                <div style={{ ...labelStyle, marginTop: 12 }}><span>Body (English)</span><Counter value={form.bodyEn} max={LIMITS.body} /></div>
                                                <textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical" }} value={form.bodyEn} maxLength={LIMITS.body} onChange={(e) => set("bodyEn", e.target.value)} placeholder="We open at 2pm all week." />
                                                {errors.bodyEn && <div style={errStyle}>{errors.bodyEn}</div>}
                                            </>
                                        ) : (
                                            <>
                                                <div style={labelStyle}><span>Message (English)</span></div>
                                                <ReactQuill theme="snow" value={form.htmlEn} onChange={(v) => set("htmlEn", v)} modules={QUILL_MODULES} formats={QUILL_FORMATS} style={{ minHeight: 140 }} />
                                                {errors.htmlEn && <div style={errStyle}>{errors.htmlEn}</div>}
                                            </>
                                        )}
                                    </div>
                                    {form.withArabic && (
                                        <div>
                                            {isText ? (
                                                <>
                                                    <div style={labelStyle}><span>Title (Arabic)</span><Counter value={form.titleAr} max={LIMITS.title} /></div>
                                                    <input style={{ ...inputStyle, direction: "rtl" }} value={form.titleAr} maxLength={LIMITS.title} onChange={(e) => set("titleAr", e.target.value)} />
                                                    {errors.titleAr && <div style={errStyle}>{errors.titleAr}</div>}
                                                    <div style={{ ...labelStyle, marginTop: 12 }}><span>Body (Arabic)</span><Counter value={form.bodyAr} max={LIMITS.body} /></div>
                                                    <textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical", direction: "rtl" }} value={form.bodyAr} maxLength={LIMITS.body} onChange={(e) => set("bodyAr", e.target.value)} />
                                                    {errors.bodyAr && <div style={errStyle}>{errors.bodyAr}</div>}
                                                </>
                                            ) : (
                                                <>
                                                    <div style={labelStyle}><span>Message (Arabic)</span></div>
                                                    <div dir="rtl">
                                                        <ReactQuill theme="snow" value={form.htmlAr} onChange={(v) => set("htmlAr", v)} modules={QUILL_MODULES} formats={QUILL_FORMATS} style={{ minHeight: 140 }} />
                                                    </div>
                                                    {errors.htmlAr && <div style={errStyle}>{errors.htmlAr}</div>}
                                                </>
                                            )}
                                            <div style={hintStyle}>Shown to customers using the app in Arabic; everyone else sees the English version.</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Image — required for an image banner, optional header art otherwise */}
                            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                                    Image {form.bannerType !== "image" && <span style={{ fontWeight: 400, color: C.textLight }}>— optional</span>}
                                </div>
                                <label style={{ ...btnSecondary, padding: "7px 14px", fontSize: 12, margin: 0 }}>
                                    {imagePreview ? "Replace…" : "Choose…"}
                                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImage} style={{ display: "none" }} />
                                </label>
                                {imagePreview && <button type="button" onClick={clearImage} style={{ ...btnSecondary, padding: "7px 14px", fontSize: 12, color: C.coral }}>Remove</button>}
                                <span style={{ fontSize: 11, color: C.textLight }}>
                                    JPEG/PNG/WebP, up to 1 MB{imageDims.width ? ` · ${imageDims.width}×${imageDims.height}` : ""}
                                </span>
                            </div>
                            {errors.image && <div style={errStyle}>{errors.image}</div>}
                        </div>

                        {/* Tap action */}
                        <div style={sectionStyle}>
                            <div style={sectionTitle}>When tapped</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {CTA_TYPES.map((t) => (
                                    <button key={t.value} type="button" onClick={() => set("ctaType", t.value)} style={pill(form.ctaType === t.value)} title={t.hint}>{t.label}</button>
                                ))}
                            </div>
                            {form.ctaType !== "none" && (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                                    <div>
                                        {form.ctaType === "screen" ? (
                                            <>
                                                <div style={labelStyle}><span>Screen</span></div>
                                                <select style={inputStyle} value={form.ctaScreen} onChange={(e) => set("ctaScreen", e.target.value)}>
                                                    {CTA_SCREENS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                </select>
                                                <div style={hintStyle}>A banner cannot open one specific product yet.</div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={labelStyle}><span>Link</span></div>
                                                <input style={inputStyle} value={form.ctaUrl} onChange={(e) => set("ctaUrl", e.target.value)} placeholder="https://example.com/offer" />
                                                {errors.ctaUrl && <div style={errStyle}>{errors.ctaUrl}</div>}
                                                <div style={hintStyle}>Opens in the phone's browser, outside the app.</div>
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <div style={labelStyle}><span>Button label (English)</span><Counter value={form.ctaLabelEn} max={LIMITS.ctaLabel} /></div>
                                        <input style={inputStyle} value={form.ctaLabelEn} maxLength={LIMITS.ctaLabel} onChange={(e) => set("ctaLabelEn", e.target.value)} placeholder="See the offer" />
                                        {errors.ctaLabelEn && <div style={errStyle}>{errors.ctaLabelEn}</div>}
                                        {form.withArabic && (
                                            <>
                                                <div style={{ ...labelStyle, marginTop: 12 }}><span>Button label (Arabic)</span></div>
                                                <input style={{ ...inputStyle, direction: "rtl" }} value={form.ctaLabelAr} maxLength={LIMITS.ctaLabel} onChange={(e) => set("ctaLabelAr", e.target.value)} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* When and how often */}
                        <div style={sectionStyle}>
                            <div style={sectionTitle}>When it runs</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                    <div style={labelStyle}><span>Starts</span></div>
                                    <input type="datetime-local" style={inputStyle} value={form.activeFrom} onChange={(e) => set("activeFrom", e.target.value)} />
                                    <div style={hintStyle}>Leave empty to start straight away.</div>
                                </div>
                                <div>
                                    <div style={labelStyle}><span>Ends</span></div>
                                    <input type="datetime-local" style={inputStyle} value={form.activeTo} min={form.activeFrom || undefined} onChange={(e) => set("activeTo", e.target.value)} />
                                    {errors.activeTo ? <div style={errStyle}>{errors.activeTo}</div> : <div style={hintStyle}>Leave empty to run until you switch it off.</div>}
                                </div>
                            </div>
                            <div style={{ ...hintStyle, marginTop: 8 }}>
                                Times are in your browser's zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
                            </div>

                            <div style={{ ...sectionTitle, marginTop: 16 }}>How often one person sees it</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {FREQUENCIES.map((f) => (
                                    <button key={f.value} type="button" onClick={() => set("frequency", f.value)} style={pill(form.frequency === f.value)} title={f.hint}>{f.label}</button>
                                ))}
                            </div>
                            <div style={hintStyle}>{FREQUENCIES.find((f) => f.value === form.frequency)?.hint}</div>
                            {form.frequency === "every_n_days" && (
                                <div style={{ marginTop: 12, maxWidth: 200 }}>
                                    <div style={labelStyle}><span>Days between showings</span></div>
                                    <input type="number" min={1} max={LIMITS.maxDays} style={inputStyle} value={form.frequencyDays} onChange={(e) => set("frequencyDays", e.target.value)} />
                                    {errors.frequencyDays && <div style={errStyle}>{errors.frequencyDays}</div>}
                                </div>
                            )}
                        </div>

                        {/* Placement and behaviour */}
                        <div style={sectionStyle}>
                            <div style={sectionTitle}>Where and how</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                    <div style={labelStyle}><span>App</span></div>
                                    <select style={inputStyle} value={form.appId} onChange={(e) => set("appId", e.target.value)}>
                                        <option value="">All linked apps ({apps.length})</option>
                                        {apps.map((a) => <option key={a.id} value={a.id}>{a.name || a.reference}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <div style={labelStyle}><span>Display order</span></div>
                                    <input type="number" style={inputStyle} value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} />
                                    <div style={hintStyle}>Lower numbers appear first in the popup.</div>
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 14 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMed, cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Switched on
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMed, cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={form.dismissible} onChange={(e) => set("dismissible", e.target.checked)} /> Customer can close it
                                </label>
                            </div>
                            {!form.dismissible && (
                                <div style={{ ...hintStyle, color: C.orange, fontWeight: 600 }}>
                                    With no close button the customer cannot reach the app until they tap the action. Keep this for maintenance notices only.
                                </div>
                            )}
                        </div>

                        {isEdit && (
                            <div style={{ ...sectionStyle, borderColor: willReshow ? C.coralLight : C.borderLight }}>
                                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: C.textMed, cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={bumpRevision} onChange={(e) => setBumpOverride(e.target.checked)} style={{ marginTop: 2 }} />
                                    <span>
                                        <b style={{ color: C.text }}>Show it again to people who already saw it</b>
                                        <div style={hintStyle}>
                                            {willReshow
                                                ? "You changed what customers see, so this is on by default. Turn it off for a typo fix nobody needs to re-read."
                                                : "You only changed the schedule or settings, so by default nobody sees it twice. Turn this on to re-show it anyway."}
                                        </div>
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    <div style={{ position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Preview</div>
                            <PhonePreview
                                type={form.bannerType}
                                title={form.titleEn}
                                body={form.bodyEn}
                                html={form.htmlEn}
                                imageUrl={imagePreview}
                                ctaLabel={previewCtaLabel}
                                dismissible={form.dismissible}
                            />
                        </div>
                        {form.withArabic && (
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Arabic</div>
                                <PhonePreview
                                    type={form.bannerType}
                                    title={form.titleAr}
                                    body={form.bodyAr}
                                    html={form.htmlAr}
                                    imageUrl={imagePreview}
                                    ctaLabel={form.ctaType !== "none" ? (form.ctaLabelAr || previewCtaLabel) : ""}
                                    dismissible={form.dismissible}
                                    dir="rtl"
                                />
                            </div>
                        )}
                        <div style={hintStyle}>
                            An approximation — the app applies its own fonts and spacing, and formatting is cleaned up when you save.
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                <button type="button" onClick={onClose} disabled={saving} style={btnSecondary}>Cancel</button>
                <button type="button" onClick={handleSubmit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                    {saving ? "Saving…" : isEdit ? "Save banner" : "Create banner"}
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default BannerComposerModal;
